import useReranker from 'new/elements/flows/ModalConfigureReranker/useReranker';
import { useCallback, useEffect } from 'react';
import usePaging from 'new/state/paging/repository/usePaging';
import useNexsetsAdapter from 'new/state/nexsets/useNexsetsAdapter';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import useRagFlowId from 'new/components/flows/types/genai_rag/context/RagFlowId/useRagFlowId';
import { tryCatchAsync } from 'new/utils/TryCatch';

const codeContainerPayload = {
  name: 'Reranker Tx',
  description: '',
  reusable: false,
  resource_type: 'ai_function',
  ai_function_type: 'reranker',
  output_type: 'custom',
  code_type: 'python',
  code_encoding: 'base64',
  code: 'ZGVmIGZ1bigpOgogICAgcGFzcw==',
  custom_config: {},
};

export default function useConfigReranker({ parentNexset, editingNexset }) {
  const { data, updateFormData, credentialId, createRerankerNode, createCodeContainer } =
    useReranker();

  const { updateNexset } = useNexsetsAdapter();
  const { refreshPipelineById } = usePipelinesAdapter();
  const ragFlowId = useRagFlowId();

  const { getMetaInfoById } = usePaging();

  useEffect(() => {
    updateFormData({
      data: {
        ...data,
      },
    });
  }, []);

  const buildDataSetPayload = (codeContainer: any) => {
    if (!parentNexset?.id) return;
    if (!credentialId) return;

    return {
      name: 'Reranker',
      description: `parent from ${parentNexset.id}`,
      parent_data_set_id: parentNexset.id,
      code_container_id: codeContainer.id,
      data_credentials_id: credentialId,
      custom_config: {
        props: data,
      },
    };
  };

  /**
   * 1. create the code_container
   * 2. create the data_set
   *    2.1 associate the code_container to data_set
   *        {
   *          "code_container_id": data_set_id
   *        }
   *
   * @missing
   *  add the data_credential_id to code_container
   */
  const addDataSet = useCallback(async () => {
    const { data: codeContainer, error: codeContainerError } =
      await createCodeContainer(codeContainerPayload);

    if (codeContainerError) return;

    const dataSetPayload = buildDataSetPayload(codeContainer);

    const { error: rerankerError, data: reranker } = await createRerankerNode(dataSetPayload);

    if (rerankerError) return;

    if (ragFlowId) {
      refreshPipelineById(Number(ragFlowId));
    }
    return !!reranker;
  }, [ragFlowId, data, parentNexset, credentialId]);

  /**
   * 1. create the code_container
   * 2. create the data_set
   *    2.1 associate the code_container to data_set
   *        {
   *          "code_container_id": data_set_id
   *        }
   *
   * @missing
   *  add the data_credential_id to code_container
   */
  const editDataSet = useCallback(async () => {
    const payload = {
      data_credentials_id: credentialId,
      custom_config: {
        ...editingNexset.custom_config,
        props: data,
      },
    };

    const { error: rerankerError, data: reranker } = await tryCatchAsync(
      updateNexset(editingNexset.id, payload),
    );

    if (rerankerError) return;
    if (ragFlowId) {
      refreshPipelineById(Number(ragFlowId));
    }
    return !!reranker;
  }, [editingNexset, credentialId, data, ragFlowId]);

  return useCallback(async () => {
    try {
      if (!editingNexset) {
        return await addDataSet();
      }
      return await editDataSet();
    } catch (e) {
    } finally {
      await refreshPipelineById(parentNexset?.origin_node_id);
    }
  }, [addDataSet, editDataSet]);
}
